import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import AcknowledgmentEmailEditor from "../components/AcknowledgmentEmailEditor";
import { Plus, MagnifyingGlass, ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Settings → Email Templates → Acknowledgment Email
 * Lists tenant default + per-client overrides. Lets admin add a new override
 * by selecting a client.
 */
export default function AcknowledgmentEmailSettings() {
  const [overrides, setOverrides] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);  // {client_id, label} or {client_id: ""} for tenant default
  const [pickClient, setPickClient] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: ov }, { data: cs }] = await Promise.all([
        API.get("/email-templates/acknowledgment/clients-with-overrides"),
        API.get("/clients"),
      ]);
      setOverrides(ov || []);
      setClients(cs?.clients || cs || []);
    } catch {
      toast.error("Failed to load templates");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const overrideClientIds = new Set(overrides.map(o => o.client_id));
  const availableClients = clients
    .filter(c => !overrideClientIds.has(c.id))
    .filter(c => {
      if (!search) return true;
      const t = search.toLowerCase();
      return (c.company_name || "").toLowerCase().includes(t) ||
             `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase().includes(t);
    });

  if (editing !== null) {
    return (
      <div className="space-y-4" data-testid="ack-email-settings-editor">
        <Button variant="outline" size="sm" onClick={() => { setEditing(null); load(); }} data-testid="ack-email-back-btn">
          <ArrowLeft size={14} className="mr-1" /> Back to list
        </Button>
        <AcknowledgmentEmailEditor clientId={editing.client_id} clientLabel={editing.label} />
      </div>
    );
  }

  const clientLabel = (id) => {
    const c = clients.find(x => x.id === id);
    if (!c) return id;
    return c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "(unnamed)";
  };

  return (
    <div className="space-y-5" data-testid="ack-email-settings">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2 font-[Chivo]">
          <EnvelopeSimple size={18} /> Acknowledgment Email Templates
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          The "we're on it" email sent automatically when a Request or Job is acknowledged.
          Set a tenant default below; override per-client where needed.
        </p>
      </div>

      {/* Tenant default card */}
      <button
        type="button"
        onClick={() => setEditing({ client_id: "", label: "Tenant Default" })}
        className="w-full text-left border-2 border-primary/40 hover:border-primary rounded-md p-4 bg-primary/5 transition-colors"
        data-testid="ack-email-tenant-default-card"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary font-semibold">Tenant default</p>
            <p className="text-base font-semibold mt-1">Edit default template →</p>
            <p className="text-xs text-muted-foreground mt-0.5">Used for any client without a custom override.</p>
          </div>
          <EnvelopeSimple size={28} className="text-primary opacity-30" />
        </div>
      </button>

      {/* Per-client overrides */}
      <div>
        <h3 className="text-sm font-semibold mb-2">Per-client overrides ({overrides.length})</h3>
        {loading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : overrides.length === 0 ? (
          <p className="text-xs text-muted-foreground border border-dashed border-border rounded p-4 text-center">
            No client overrides yet. All clients use the tenant default.
          </p>
        ) : (
          <div className="border border-border rounded-md divide-y divide-border" data-testid="ack-email-overrides-list">
            {overrides.map(o => (
              <button
                key={o.client_id}
                type="button"
                onClick={() => setEditing({ client_id: o.client_id, label: o.client_label || clientLabel(o.client_id) })}
                className="w-full text-left p-3 hover:bg-muted/40 flex items-center justify-between"
                data-testid={`ack-email-override-${o.client_id}`}
              >
                <div>
                  <p className="text-sm font-medium">{o.client_label || clientLabel(o.client_id)}</p>
                  <p className="text-[10px] text-muted-foreground">Subject: {o.subject || "—"}</p>
                </div>
                <div className="flex items-center gap-2 text-[10px]">
                  <span className={`px-2 py-0.5 rounded-full font-medium ${o.enabled ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"}`}>
                    {o.enabled ? "Active" : "Disabled"}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Add override */}
      <div className="border border-dashed border-border rounded-md p-4 bg-muted/20">
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
          <Plus size={14} /> Create a new client override
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients without override…" className="pl-7 h-8 text-xs" data-testid="ack-email-client-search" />
          </div>
          <Select value={pickClient} onValueChange={setPickClient}>
            <SelectTrigger className="w-[260px] h-8 text-xs" data-testid="ack-email-client-select">
              <SelectValue placeholder="Pick a client…" />
            </SelectTrigger>
            <SelectContent>
              {availableClients.length === 0 && (
                <SelectItem value="__none" disabled>{clients.length === 0 ? "No clients yet" : "Every client already has an override"}</SelectItem>
              )}
              {availableClients.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  {c.company_name || `${c.first_name} ${c.last_name}`.trim() || "(unnamed)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            disabled={!pickClient}
            onClick={() => setEditing({ client_id: pickClient, label: clientLabel(pickClient) })}
            data-testid="ack-email-add-override-btn"
          >
            Configure →
          </Button>
        </div>
      </div>
    </div>
  );
}
